import { CSSTransition } from 'react-transition-group';
import { Theme } from '@emotion/react';
import {
  siteMenuBaseContent,
  siteMenuNestedContent,
  siteMenuRootContainer,
} from './siteMenuCssDefs';
import React from 'react';

export const baseMenuId = 'baseMenu';
export interface SiteMenuProps {
  /**
   * Function that builds content/layers of menu (passed in to builder prop, with access to dispatch function to control currently visible content).
   * @param {function} setSelectedId - Dispatch function to set state, keeping track of the ID of currently visible content.
   * @returns {object} - Object containing keys: content IDs, and values: content (JSX.Element / React.ReactNode).
   * NOTE: Top-layer of content must have a key of baseMenuId (exported as const from this file)
   * NOTE: It is recommended to memoize the builder function in the calling component (see SiteMenu.stories.tsx for details). However,
   * this approach is not recommended if the contents of the menu are intended to change dynamically.
   */
  builder: (setSelectedId: React.Dispatch<React.SetStateAction<string>>) => {
    [key: string]: React.ReactNode;
  };
  /**
   * Optionally determines which content page the menu should render first (e.g., if the "account" nested view should be rendered)
   */
  showInitialContentId?: string;
}

export const SITE_MENU_CSS_TRANSITION_TIME = 150;

const SiteMenu: React.FunctionComponent<SiteMenuProps> = ({
  builder,
  showInitialContentId = baseMenuId,
  ...rest
}) => {
  // Track currently visible content
  const [selectedId, setSelectedId] = React.useState(showInitialContentId);

  const menuBuilderResults = React.useMemo(() => {
    return builder(setSelectedId);
  }, [builder]);

  const baseMenu = (
    <CSSTransition
      classNames="site-menu"
      in={selectedId === 'baseMenu'}
      timeout={SITE_MENU_CSS_TRANSITION_TIME}
      unmountOnExit
    >
      <nav
        css={(theme: Theme) => [siteMenuBaseContent(theme), siteMenuRootContainer(theme)]}
        {...rest}
      >
        {menuBuilderResults['baseMenu']}
      </nav>
    </CSSTransition>
  );

  const nestedMenus = Object.keys(menuBuilderResults)
    .filter((key) => key !== 'baseMenu')
    .map((key) => {
      return (
        <CSSTransition
          classNames="site-menu-nested"
          key={key}
          in={selectedId === key}
          timeout={SITE_MENU_CSS_TRANSITION_TIME}
          unmountOnExit
        >
          <nav
            css={(theme: Theme) => [siteMenuNestedContent(theme), siteMenuRootContainer(theme)]}
            {...rest}
          >
            {menuBuilderResults[key]}
          </nav>
        </CSSTransition>
      );
    });

  return (
    <>
      {baseMenu}
      {nestedMenus}
    </>
  );
};

export default SiteMenu;
